import { Box, FormControl, FormErrorMessage, FormHelperText, FormLabel, Spinner } from "@chakra-ui/react";
import type { Content, Editor } from "@tiptap/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiDraftModal } from "../../../../../common/ai/AiDraftModal";
import { CpvSelector } from "../../../../../common/cpv/CpvSelector";
import { TipTap, parseTiptapContent } from "../../../../../common/input/TipTap/TipTap";
import { getMarkdownFromTiptapContent } from "../../../../../common/input/TipTap/markdownUtils";
import { buildDescriptionMessages } from "../utils/descriptionAiPrompt";
import { ContractPeriodSection } from "../components/ContractPeriodSection";
import { AdvancedSettingsSection } from "../components/AdvancedSettingsSection";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { type SourcingEventV2Dto, type SourcingEventV2PatchDto } from "../../../../../autogen/bff-api";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const ClassificationStep = ({ eventId, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t, i18n } = useTranslation();
  const { state, updateField, fieldStatus, fieldError } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();
  const descriptionEditorRef = useRef<Editor | null>(null);
  const [showDescriptionAi, setShowDescriptionAi] = useState(false);

  const title = state.eventData.title;
  const description = state.eventData.description ?? "";
  const cpvCodes = state.publicContext.cpvCodes ?? [];
  const cpvFieldError = fieldError("publicContext.cpvCodes");
  const cpvHasAnyError = fieldStatus("publicContext.cpvCodes") === "error";
  const isSaving = (field: string) => fieldStatus(field) === "saving" || fieldStatus(field) === "pending";

  return (
    <Box pb="8" pt="10">
      <Box borderWidth="1px" borderRadius="lg" p="5" mb="8">
        <FormControl mb="2">
          <FormLabel>
            {t("Description")} {isSaving("eventData.description") && <Spinner size="xs" />}
          </FormLabel>
          <TipTap
            content={description}
            onUpdate={async (value) => updateField({ eventData: { description: value } })}
            editable={true}
            debounceWait={300}
            onEditorReady={(editor) => {
              descriptionEditorRef.current = editor;
            }}
            onAutofill={() => setShowDescriptionAi(true)}
          />
          <FormHelperText>{t("Describe the scope and requirements of this procurement")}</FormHelperText>
          {showDescriptionAi && (
            <AiDraftModal
              title={t("Suggest description with AI")}
              feature="se-public-description"
              buildMessages={() =>
                buildDescriptionMessages({
                  title,
                  currentDescription: getMarkdownFromTiptapContent(description),
                  contractType: state.publicContext.contractNature ?? undefined,
                  cpvCodes,
                  estimatedValueNok: state.publicContext.estimatedValue?.value ?? null,
                  language: i18n.language.startsWith("no") ? "no" : "en",
                })
              }
              onClose={() => setShowDescriptionAi(false)}
              onInsert={(json) => {
                descriptionEditorRef.current?.commands.setContent(parseTiptapContent(json) as Content, {
                  emitUpdate: true,
                });
                setShowDescriptionAi(false);
              }}
            />
          )}
        </FormControl>
      </Box>

      <Box borderWidth="1px" borderRadius="lg" p="5" mb="8" bg="smBackground">
        <FormControl isInvalid={cpvHasAnyError}>
          <FormLabel>
            {t("CPV codes")} {isSaving("publicContext.cpvCodes") && <Spinner size="xs" />}
          </FormLabel>
          <CpvSelector
            value={cpvCodes}
            onChange={(next) => updateField({ publicContext: { cpvCodes: next } }, { debounceMs: 300 })}
            isInvalid={cpvHasAnyError}
            suggestionContext={{ title, description, contractType: state.publicContext.contractNature ?? undefined }}
          />
          {cpvFieldError && <FormErrorMessage>{formatFieldError(cpvFieldError)}</FormErrorMessage>}
        </FormControl>
      </Box>

      <ContractPeriodSection eventId={eventId} />
      <AdvancedSettingsSection eventId={eventId} />

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
